import {
    AirplayIcon,
    ArrowLeftIcon,
    ArrowRightIcon,
    BanknoteIcon,
    DrumIcon,
    EyeIcon,
    GuitarIcon,
    HandIcon,
    MusicIcon,
    PlayIcon,
    RadioIcon,
} from 'lucide-react-native';
import { useState } from 'react';
import { View, XStack, YStack } from 'tamagui';

import {
    BackButton,
    BackButtonIconType,
} from '../../components/buttons/BackButton';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    IconButton,
    InfoButton,
    PillButton,
    TabButton,
} from '../../components/buttons/Button';
import { CloseButton } from '../../components/buttons/CloseButton';
import { TileButton } from '../../components/buttons/TileButton';
import { Icon } from '../../components/icons/Icon';
import { SegmentedControl } from '../../components/segmented-controls/SegmentedControl';
import {
    BodyMediumRegular,
    BodySmallRegular,
    H6Regular,
} from '../../components/text/Typography';

const iconColor = (isSelected: boolean) =>
    isSelected ? '$black' : '$gray1000';

export const Buttons = () => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [disabled, setDisabled] = useState(false);
    const [size, setSize] = useState<ButtonSize.SMALL | ButtonSize.LARGE>(
        ButtonSize.LARGE,
    );

    return (
        <YStack flex={1} gap={16}>
            <XStack gap={16} marginBottom={8}>
                <SegmentedControl
                    options={[
                        { label: 'Large', value: ButtonSize.LARGE },
                        { label: 'Small', value: ButtonSize.SMALL },
                    ]}
                    defaultValue={size as string}
                    selected={size as string}
                    onValueChange={(val: string) => {
                        setSize(val as ButtonSize.SMALL | ButtonSize.LARGE);
                    }}
                    size="small"
                    width="auto"
                />
                <SegmentedControl
                    options={[
                        { label: 'Enabled', value: 'false' },
                        { label: 'Disabled', value: 'true' },
                    ]}
                    defaultValue={disabled ? 'true' : 'false'}
                    selected={disabled ? 'true' : 'false'}
                    onValueChange={(val: string) => {
                        setDisabled(val === 'true');
                    }}
                    size="small"
                    width="auto"
                />
            </XStack>
            <YStack gap={8} flex={1}>
                <H6Regular>Variants</H6Regular>

                <YStack $md={{ flexDirection: 'row' }} gap={8} width="100%">
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={size}
                        text="Primary"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={size}
                        text="Secondary"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        size={size}
                        text="Tertiary"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.PREMIUM}
                        size={size}
                        text="Premium"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.PREMIUM_SECONDARY}
                        size={size}
                        text="Premium Secondary"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.PREMIUM_TERTIARY}
                        size={size}
                        text="Premium Tertiary"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                </YStack>
                <YStack $md={{ flexDirection: 'row' }} gap={8}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        tone="positive"
                        size={size}
                        text="Primary - Positive"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        tone="positive"
                        size={size}
                        text="Secondary - Positive"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        tone="positive"
                        size={size}
                        text="Tertiary - Positive"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                </YStack>
                <YStack $md={{ flexDirection: 'row' }} gap={8}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        tone="danger"
                        size={size}
                        text="Primary - Danger"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        tone="danger"
                        size={size}
                        text="Secondary - Danger"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        tone="danger"
                        size={size}
                        text="Tertiary - Danger"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                </YStack>
                <YStack gap={8}>
                    <BodyMediumRegular>On a background</BodyMediumRegular>
                    <YStack
                        $md={{ flexDirection: 'row' }}
                        gap={8}
                        backgroundColor="$gray300"
                        padding={24}
                        borderRadius={24}
                    >
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={size}
                            text="Primary"
                            disabled={disabled}
                            $md={{ flex: 1 }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY_ALT}
                            size={size}
                            text="Secondary Alt"
                            disabled={disabled}
                            $md={{ flex: 1 }}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={size}
                            text="Secondary"
                            disabled={disabled}
                            $md={{ flex: 1 }}
                        />
                        <Button
                            variant={ButtonVariant.TERTIARY}
                            size={size}
                            text="Tertiary"
                            disabled={disabled}
                            $md={{ flex: 1 }}
                        />
                        <Button
                            variant={ButtonVariant.PREMIUM}
                            size={size}
                            text="Premium"
                            disabled={disabled}
                            $md={{ flex: 1 }}
                        />
                        <Button
                            variant={ButtonVariant.PREMIUM_SECONDARY}
                            size={size}
                            text="Premium Secondary"
                            disabled={disabled}
                            $md={{ flex: 1 }}
                        />
                    </YStack>
                </YStack>
                <YStack gap={8}>
                    <BodyMediumRegular>On Premium surfaces</BodyMediumRegular>
                    <YStack
                        $md={{ flexDirection: 'row' }}
                        gap={8}
                        backgroundColor="$peachLight"
                        padding={24}
                        borderRadius={24}
                    >
                        <Button
                            variant={ButtonVariant.PREMIUM_SECONDARY}
                            size={size}
                            text="Upgrade to Premium"
                            disabled={disabled}
                            $md={{ flex: 1 }}
                        />
                        <YStack
                            backgroundColor="$peachLightest"
                            padding={16}
                            borderRadius={16}
                            $md={{ flex: 1 }}
                        >
                            <Button
                                variant={ButtonVariant.PREMIUM_SECONDARY}
                                size={size}
                                text="On peachLightest"
                                disabled={disabled}
                            />
                        </YStack>
                        <Button
                            variant={ButtonVariant.PREMIUM_TERTIARY}
                            size={size}
                            text="Premium Tertiary"
                            disabled={disabled}
                            $md={{ flex: 1 }}
                        />
                    </YStack>
                    <YStack
                        $md={{ flexDirection: 'row' }}
                        gap={8}
                        backgroundColor="$peachDarkest"
                        padding={24}
                        borderRadius={24}
                    >
                        <Button
                            variant={ButtonVariant.PREMIUM}
                            size={size}
                            text="Premium"
                            disabled={disabled}
                            $md={{ flex: 1 }}
                        />
                        <Button
                            variant={ButtonVariant.PREMIUM_TERTIARY}
                            size={size}
                            text="Premium Tertiary (light text)"
                            textProps={{ color: '$peachLight' }}
                            disabled={disabled}
                            $md={{ flex: 1 }}
                        />
                    </YStack>
                </YStack>
            </YStack>
            <YStack gap={8} flex={1}>
                <H6Regular>With icons</H6Regular>
                <YStack gap={8} $lg={{ flexDirection: 'row' }}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={size}
                        text="Icon Left"
                        icon={AirplayIcon}
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={size}
                        text="Icon Right"
                        icon={RadioIcon}
                        iconPosition="right"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={size}
                        text="Icon with super long button label text !!!"
                        icon={AirplayIcon}
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        size={size}
                        text="Icon Left"
                        icon={AirplayIcon}
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        size={size}
                        text="Icon Right"
                        icon={RadioIcon}
                        iconPosition="right"
                        disabled={disabled}
                        $md={{ flex: 1 }}
                    />
                </YStack>
            </YStack>

            <YStack gap={8} flex={1}>
                <H6Regular>The &quot;auto&quot; width option</H6Regular>
                <XStack gap={8}>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={size}
                        width="auto"
                        text='width="auto"'
                        disabled={disabled}
                    />
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={size}
                        width="auto"
                        text='width="auto"'
                        disabled={disabled}
                    />
                    <Button
                        variant={ButtonVariant.TERTIARY}
                        size={size}
                        width="auto"
                        text='width="auto"'
                        disabled={disabled}
                    />
                </XStack>
            </YStack>

            <YStack gap={8} flex={1}>
                <H6Regular>IconButton</H6Regular>
                <XStack gap={8} alignItems="center">
                    <IconButton
                        variant="primary"
                        icon={GuitarIcon}
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <IconButton
                        variant="primary"
                        icon={GuitarIcon}
                        onPress={() => {}}
                        disabled={disabled}
                        size="tiny"
                    />
                    <IconButton
                        variant="secondary"
                        icon={DrumIcon}
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <IconButton
                        variant="secondary"
                        icon={DrumIcon}
                        onPress={() => {}}
                        disabled={disabled}
                        size="tiny"
                    />
                    <IconButton
                        variant="tertiary"
                        icon={MusicIcon}
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <IconButton
                        variant="tertiary"
                        icon={MusicIcon}
                        onPress={() => {}}
                        disabled={disabled}
                        size="tiny"
                    />
                    <BodyMediumRegular>Default</BodyMediumRegular>
                </XStack>
                <XStack gap={8} alignItems="center">
                    <IconButton
                        variant="primary"
                        tone="positive"
                        icon={GuitarIcon}
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <IconButton
                        variant="primary"
                        tone="positive"
                        icon={GuitarIcon}
                        onPress={() => {}}
                        disabled={disabled}
                        size="tiny"
                    />
                    <IconButton
                        variant="secondary"
                        tone="positive"
                        icon={DrumIcon}
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <IconButton
                        variant="secondary"
                        tone="positive"
                        icon={DrumIcon}
                        onPress={() => {}}
                        disabled={disabled}
                        size="tiny"
                    />
                    <IconButton
                        variant="tertiary"
                        tone="positive"
                        icon={MusicIcon}
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <IconButton
                        variant="tertiary"
                        tone="positive"
                        icon={MusicIcon}
                        onPress={() => {}}
                        disabled={disabled}
                        size="tiny"
                    />
                    <BodyMediumRegular>
                        tone=&quot;positive&quot;
                    </BodyMediumRegular>
                </XStack>
                <XStack gap={8} alignItems="center">
                    <IconButton
                        variant="primary"
                        tone="danger"
                        icon={GuitarIcon}
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <IconButton
                        variant="primary"
                        tone="danger"
                        icon={GuitarIcon}
                        onPress={() => {}}
                        disabled={disabled}
                        size="tiny"
                    />
                    <IconButton
                        variant="secondary"
                        tone="danger"
                        icon={DrumIcon}
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <IconButton
                        variant="secondary"
                        tone="danger"
                        icon={DrumIcon}
                        onPress={() => {}}
                        disabled={disabled}
                        size="tiny"
                    />
                    <IconButton
                        variant="tertiary"
                        tone="danger"
                        icon={MusicIcon}
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <IconButton
                        variant="tertiary"
                        tone="danger"
                        icon={MusicIcon}
                        onPress={() => {}}
                        disabled={disabled}
                        size="tiny"
                    />
                    <BodyMediumRegular>
                        tone=&quot;danger&quot;
                    </BodyMediumRegular>
                </XStack>
            </YStack>
            <YStack gap={8} flex={1}>
                <H6Regular>
                    InfoButton (default and &quot;subtle&quot;)
                </H6Regular>
                <XStack gap={8}>
                    <InfoButton onPress={() => {}} />
                    <InfoButton subtle onPress={() => {}} />
                    <InfoButton text="A label" onPress={() => {}} />
                    <InfoButton text="Another" subtle onPress={() => {}} />
                </XStack>
            </YStack>

            <YStack gap={8} flex={1}>
                <H6Regular>PillButton</H6Regular>
                <XStack gap={16} flexWrap="wrap">
                    <PillButton
                        variant="primary"
                        text="Primary"
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <PillButton
                        variant="primary"
                        text="Primary icon"
                        icon={PlayIcon}
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <PillButton
                        text="No icon"
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <PillButton
                        text="Icon left"
                        icon={ArrowLeftIcon}
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <PillButton
                        text="Icon right"
                        icon={ArrowRightIcon}
                        iconPosition="right"
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <PillButton
                        variant="tertiary"
                        text="Tertiary"
                        onPress={() => {}}
                        disabled={disabled}
                    />
                    <PillButton
                        variant="tertiary"
                        text="Tertiary icon"
                        icon={BanknoteIcon}
                        onPress={() => {}}
                        disabled={disabled}
                    />
                </XStack>
            </YStack>
            <YStack>
                <H6Regular>TabButton</H6Regular>
                <BodySmallRegular>
                    Currently designed and built only for the SideNav component,
                    but can be adapted if this pattern is desired for other
                    places
                </BodySmallRegular>
                <YStack gap={8}>
                    <View width={140}>
                        <TabButton
                            text="Rock"
                            icon={
                                <Icon
                                    size={20}
                                    icon={HandIcon}
                                    color={iconColor(selectedTab === 0)}
                                />
                            }
                            isSelected={selectedTab === 0}
                            onPress={() => {
                                setSelectedTab(0);
                            }}
                        />
                    </View>
                    <View width={140}>
                        <TabButton
                            text="Paper"
                            icon={
                                <Icon
                                    size={20}
                                    icon={HandIcon}
                                    color={iconColor(selectedTab === 1)}
                                />
                            }
                            isSelected={selectedTab === 1}
                            onPress={() => {
                                setSelectedTab(1);
                            }}
                        />
                    </View>
                    <View width={140}>
                        <TabButton
                            text="Scissors"
                            icon={
                                <Icon
                                    size={20}
                                    icon={HandIcon}
                                    color={iconColor(selectedTab === 2)}
                                />
                            }
                            isSelected={selectedTab === 2}
                            onPress={() => {
                                setSelectedTab(2);
                            }}
                        />
                    </View>
                </YStack>
            </YStack>

            <YStack gap={16}>
                <H6Regular>TileButton</H6Regular>
                <BodySmallRegular>
                    TileButtons currently need to be within a container that
                    specifies a height for the buttons to fill
                </BodySmallRegular>
                <XStack
                    gap={16}
                    height={60}
                    marginHorizontal={40}
                    maxWidth={500}
                >
                    <TileButton text="No icon" onPress={() => {}} />
                    <TileButton text="Icon" icon={EyeIcon} onPress={() => {}} />
                </XStack>
            </YStack>

            <YStack gap={16}>
                <H6Regular>BackButton</H6Regular>
                <BodySmallRegular>
                    BackButton provides navigation back functionality with
                    different icon types and background options.
                </BodySmallRegular>
                <YStack gap={12}>
                    <XStack gap={12} alignItems="center">
                        <BackButton
                            background="transparent"
                            iconType={BackButtonIconType.LEFT}
                            customBackFn={() => {}}
                        />
                        <BodySmallRegular>
                            background=transparent, iconType=left
                        </BodySmallRegular>
                    </XStack>
                    <XStack gap={12} alignItems="center">
                        <BackButton
                            background="gray"
                            iconType={BackButtonIconType.LEFT}
                            customBackFn={() => {}}
                        />
                        <BodySmallRegular>
                            background=gray, iconType=left
                        </BodySmallRegular>
                    </XStack>
                    <XStack gap={12} alignItems="center">
                        <BackButton
                            background="transparent"
                            iconType={BackButtonIconType.ARROW_LEFT}
                            customBackFn={() => {}}
                        />
                        <BodySmallRegular>
                            background=transparent, iconType=arrowLeft
                        </BodySmallRegular>
                    </XStack>
                    <XStack gap={12} alignItems="center">
                        <BackButton
                            background="gray"
                            iconType={BackButtonIconType.ARROW_LEFT}
                            customBackFn={() => {}}
                        />
                        <BodySmallRegular>
                            background=gray, iconType=arrowLeft
                        </BodySmallRegular>
                    </XStack>
                    <XStack gap={12} alignItems="center">
                        <BackButton
                            background="transparent"
                            iconType={BackButtonIconType.X}
                            customBackFn={() => {}}
                        />
                        <BodySmallRegular>
                            background=transparent, iconType=x
                        </BodySmallRegular>
                    </XStack>
                    <XStack gap={12} alignItems="center">
                        <BackButton
                            background="gray"
                            iconType={BackButtonIconType.X}
                            customBackFn={() => {}}
                        />
                        <BodySmallRegular>
                            background=gray, iconType=x
                        </BodySmallRegular>
                    </XStack>
                </YStack>
            </YStack>

            <YStack gap={16}>
                <H6Regular>CloseButton</H6Regular>
                <BodySmallRegular>
                    CloseButton provides a close/dismiss functionality with
                    different background options.
                </BodySmallRegular>
                <YStack gap={12}>
                    <XStack gap={12} alignItems="center">
                        <CloseButton
                            background="transparent"
                            onPress={() => {}}
                        />
                        <BodySmallRegular>
                            background=transparent
                        </BodySmallRegular>
                    </XStack>
                    <XStack gap={12} alignItems="center">
                        <CloseButton background="gray" onPress={() => {}} />
                        <BodySmallRegular>background=gray</BodySmallRegular>
                    </XStack>
                </YStack>
            </YStack>
        </YStack>
    );
};
